import React, { useEffect, useState } from "react";
export { default as NotificationsMenu } from './NotificationsMenu';
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Pagination from '../Pagination';
import { Notification } from "../../models/general";
import { createUrl } from "../../utils";
import Icon from "../AppIcon";
import { notificationIconMap as iconMap } from './notificationIconMap';
import Button from "../ui/Button";
import { cn } from "../../utils/cn";
import dateFormat from "dateformat";
import { GetNotificationsFirstPagePerUser, GetNotificationsPerUser, markNotificationAsRead } from "../../api/General/Notification";

interface INotificationListProps {
  type?: number | undefined;
  full?: boolean;
  showHeader?: boolean;
  markAllReadSignal?: number;
  onUnreadCountChange?: (count: number) => void;
  refreshSignal?: number;
}

const NotificationList = (props: INotificationListProps) => {
  const { type, full, showHeader = true, markAllReadSignal, refreshSignal, onUnreadCountChange } = props;
  const { t } = useTranslation();
  const user = useSelector((state: any) => state.user);
  const userId = useSelector((state: any) => state.user.userId);
  const navigate = useNavigate();

  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsTotalCount, setNotificationsTotalCount] = useState(0);
  const [notificationsPageCount] = useState(6);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  useEffect(() => {
    setSelectedPageIndex(0);
    loadNotifications(type, 0, notificationsPageCount);
  }, [type, userId]);

  // When parent triggers mark-all-read, update local list instantly for UX
  useEffect(() => {
    if (markAllReadSignal) {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    }
  }, [markAllReadSignal]);

  // External refresh trigger (e.g., page-level reload button)
  useEffect(() => {
    if (typeof refreshSignal !== 'undefined') {
      setSelectedPageIndex(0);
      loadNotifications(type, 0, notificationsPageCount);
    }
  }, [refreshSignal]);

  const loadNotifications = async (notificationType: number | undefined, pageNumber: number, pageSize: number) => {
    if (!userId) {
      console.error("User ID is not available");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { payload } = full ?
        await GetNotificationsPerUser(userId, notificationType ?? 0, pageNumber + 1, pageSize)
        : await GetNotificationsFirstPagePerUser(userId);
      setNotifications(payload.notificationsList);
      if (onUnreadCountChange) {
        const unread = payload.notificationsList.filter((n: any) => !n.isRead).length;
        onUnreadCountChange(unread);
      }
      setNotificationsTotalCount(payload.notificationsTotalCount);
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await markNotificationAsRead(id);
      setNotifications(prev => {
        const next = prev.map(n => n.id === id ? { ...n, isRead: true } : n);
        if (onUnreadCountChange) onUnreadCountChange(next.filter(n => !n.isRead).length);
        return next;
      });
    } catch (e) {
      console.error(e);
    }
  };

  const markAllAsRead = async () => {
    const unread = notifications.filter(n => !n.isRead);
    if (!unread.length) return;
    // Fire & wait sequentially to avoid hammering backend
    for (const n of unread) {
      try { await markNotificationAsRead(n.id); } catch (e) { console.error(e); }
    }
    setNotifications(prev => {
      const next = prev.map(n => ({ ...n, isRead: true }));
      if (onUnreadCountChange) onUnreadCountChange(0);
      return next;
    });
  };

  const relativeTime = (date: Date) => {
    const diffMs = Date.now() - date.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d`;
    const wk = Math.floor(day / 7);
    if (wk < 4) return `${wk}w`;
    const mo = Math.floor(day / 30);
    if (mo < 12) return `${mo}mo`;
    const yr = Math.floor(day / 365);
    return `${yr}y`;
  };

  function createListItem(item: Notification) {
    const iconName = iconMap[item.notificationType?.name] || 'Info';
    const createdLocal = new Date(new Date(item.createdOn).getTime() - (new Date().getTimezoneOffset() * 60 * 1000));
    const timeFormatted = dateFormat(createdLocal, full ? 'dd/mm/yyyy h:MM:ss TT' : 'dd mmm h:MM TT');

    return (
      <div
        key={item.id}
        className={cn('transition-colors duration-200', !item.isRead && 'bg-accent')}
      >
        <button
          className="w-full flex items-start gap-3 py-3 px-4 text-start hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-inset"
          onClick={async () => {
            await markAsRead(item.id);
            const url = createUrl(item);
            if (url) navigate(url);
          }}
        >
          <div className="relative flex items-start">
            <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center">
              <Icon name={iconName} size={24} style={{ color: user?.companyPrimaryColor }} />
            </div>
            {!item.isRead && (
              <div
                className="absolute top-0 end-0 w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: user?.companyPrimaryColor || 'var(--primary)' }}
              />
            )}
          </div>
          <div className="flex-1 min-w-0 me-1">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-sm font-semibold text-foreground leading-tight">{t(item.title ?? '', { ...(item.titleParams ? JSON.parse(item.titleParams) : {}), nsSeparator: false })}</span>
              <span className="text-xs text-muted-foreground" title={timeFormatted}>{relativeTime(createdLocal)}</span>
            </div>
            <span className="text-sm text-muted-foreground mt-1 block overflow-hidden text-ellipsis">
              {t(item.body ?? '', { ...(item.bodyParams ? JSON.parse(item.bodyParams) : {}), nsSeparator: false })}
            </span>
            {(() => {
              if (item.notificationType?.name !== 'AssetOtpVerification') return null;
              const parsedBody = item.bodyParams ? JSON.parse(item.bodyParams) : {};
              const otp: string | undefined = parsedBody?.otp;
              if (!otp || typeof otp !== 'string' || otp.length === 0) return null;
              if (typeof navigator === 'undefined' || !navigator.clipboard) return null;
              return (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(otp).then(() => {
                      setCopiedId(item.id);
                      setTimeout(() => setCopiedId(null), 2000);
                    }).catch(() => { /* clipboard write failed silently */ });
                  }}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 mt-1"
                >
                  {copiedId === item.id ? t('notifications.copied') : t('notifications.copyCode')}
                </button>
              );
            })()}
          </div>
        </button>
      </div>
    );
  }

  const onPageChange = (page: number): void => {
    const zeroBased = page - 1;
    setSelectedPageIndex(zeroBased);
    loadNotifications(type, zeroBased, notificationsPageCount);
  };

  const skeletons = Array.from({ length: 4 }).map((_, i) => (
    <div key={`skeleton-${i}`} className="flex items-start gap-3 py-3 px-4 animate-pulse">
      <div className="w-9 h-9 rounded-lg bg-muted flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-muted rounded w-3/5" />
        <div className="h-3 bg-muted rounded w-4/5" />
      </div>
    </div>
  ));

  const showEmpty = !loading && !notifications.length;

  return (
    <div>
      {full && showHeader && (
        <div className="mb-3">
          <div className="flex items-center justify-between flex-wrap gap-y-1">
            <h3 className="text-lg font-semibold text-foreground">{t('Notifications')}</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{notificationsTotalCount} {t('total')}</span>
              <div className="w-px h-4 bg-border" />
              <Button variant="ghost" size="sm" disabled={!notifications.some(n => !n.isRead)} onClick={markAllAsRead}>
                {t('Mark all read')}
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="w-full">
        {loading ? skeletons : notifications.map((x) => createListItem(x))}
      </div>
      {showEmpty && (
        <div className="text-center py-10 px-4">
          <Icon name="Bell" size={40} className="text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-base font-medium text-foreground">{t('You are all caught up!')}</p>
          <p className="text-sm text-muted-foreground">{t('No notifications to show')}</p>
        </div>
      )}
      {error && (
        <div className="mt-2 p-3 rounded-lg bg-destructive/10 text-destructive">
          <p className="text-sm">{error}</p>
          <Button variant="ghost" size="sm" onClick={() => loadNotifications(type, selectedPageIndex, notificationsPageCount)} className="mt-1">
            {t('Retry')}
          </Button>
        </div>
      )}
      {full && !loading && !error && notificationsTotalCount > notificationsPageCount && (
        <Pagination
          currentPage={selectedPageIndex + 1}
          totalPages={Math.max(1, Math.ceil(notificationsTotalCount / notificationsPageCount))}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

export default NotificationList;
